import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Transaction } from '../../../services/types';
import {
  GateScreen,
  GateRoot,
  GreyCard,
  SuccessRow,
  InfoRow,
  GateSpinner,
  GateStatus,
  LION_GREEN,
} from '../components';

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
`;

const CardDesc = styled.div`
  font-size: 12px;
  color: ${LION_GREEN};
  line-height: 1.5;
`;

// ─── Processing ───────────────────────────────────────────────────────────────
// ─── Ожидание без действия клиента ────────────────────────────────────────────
// Реквизит ещё подбирается, страница уходит к провайдеру или только загружается. Экран намеренно
// голый — без шапки, карточки и номера заказа: отсюда клиента может сразу увести на чужую страницу
// оплаты, и мелькнувшая перед ней наша полноценная страница выглядела бы как два разных сайта подряд.
// От общего спиннера приложения он отличается только тем, что говорит на языке шаблона: его шрифт,
// его зелёный, его серый.
const WaitingWrap = styled.div`
  margin: auto 0;
  width: 100%;
  max-width: 340px;
  padding: 24px 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
`;

const WaitingTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
`;

const WaitingText = styled.div`
  margin-top: -8px;
  font-size: 12px;
  color: #8a8a8a;
  line-height: 1.5;
`;

export const GateWaitingScreen = ({ title, text }: { title?: string; text?: string }) => (
  <GateRoot>
    <WaitingWrap>
      <GateSpinner size={40} />
      {title && <WaitingTitle>{title}</WaitingTitle>}
      {text && <WaitingText>{text}</WaitingText>}
    </WaitingWrap>
  </GateRoot>
);

export const GateProcessingScreen = ({
  transaction,
  uuid,
  timeLeft,
  onBack,
  receiptAccepted = true,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onBack: () => void;
  /** CPAYMENT-1616: во время авто-проверки чек ещё не принят — строку не показываем. */
  receiptAccepted?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <GateScreen uuid={uuid} timeLeft={timeLeft} actions={[{ label: t('common.back'), onClick: onBack, variant: 'grey', dataSentryElement: 'onepage-processing-back' }]}>
      <GreyCard>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <GateSpinner size={24} />
          <div>
            <CardTitle>{t('processing.title')}</CardTitle>
            <CardDesc>{t('gate.autoCheck')}</CardDesc>
          </div>
        </div>
      </GreyCard>
      {receiptAccepted && (
        <SuccessRow>
          {t('upload.receiptAccepted')} <span>✓</span>
        </SuccessRow>
      )}
    </GateScreen>
  );
};

// ─── Платёж на проверке ───────────────────────────────────────────────────────
// Клиент своё сделал, деньги ещё не зачислены: чек проверяют, по оплате может быть открыт диспут.
// Экран успеха здесь врал бы самым дорогим способом — клиент закрывает страницу, считая платёж
// завершённым, и о неудачной проверке узнаёт уже поддержка, а не тот, кто ещё мог что-то исправить.
// Таймера нет: у этого состояния нет срока, который страница могла бы показать.
export const GatePaymentProcessingScreen = ({
  uuid,
  disputeOpen,
  onFinish,
}: {
  uuid: string;
  disputeOpen?: boolean;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <GateScreen
      uuid={uuid}
      // Платёж не завершён, поэтому кнопка выхода называет то, что делает, а не «завершить».
      // У кнопки поддержки перехода пока нет: клиента некуда вести, она занимает своё место.
      actions={[
        ...(disputeOpen
          ? [{ label: t('gate.disputeAction'), onClick: () => {}, variant: 'green' as const, dataSentryElement: 'onepage-dispute-support' }]
          : []),
        { label: t('gate.returnToSite'), onClick: onFinish, variant: 'grey' as const, dataSentryElement: 'onepage-receipt-accepted-finish' },
      ]}
    >
      {/* При диспуте обещать автоматическое зачисление нельзя: от клиента ждут действия, и экран
          говорит только это. */}
      {disputeOpen ? (
        <GateStatus tone="warning" title={t('gate.disputeTitle')} text={t('gate.disputeText')} />
      ) : (
        <GateStatus tone="wait" title={t('gate.processingTitle')} text={t('gate.processingText')} />
      )}
    </GateScreen>
  );
};

// ─── Receipt was never accepted (CPAYMENT-1616) ───────────────────────────────
// Тикет уже у оператора, но чек так и не приняли. Экран успеха здесь врёт: клиенту нужно
// понятное действие, а не кнопка «вернуться», поэтому основная кнопка ведёт в поддержку.
export const GateReceiptUnconfirmedScreen = ({
  uuid,
  timeLeft,
  onContactSupport,
}: {
  uuid: string;
  timeLeft: string;
  onContactSupport: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <GateScreen
      uuid={uuid}
      timeLeft={timeLeft}
      actions={[{ label: t('receiptUnconfirmed.action'), onClick: onContactSupport, variant: 'green', dataSentryElement: 'onepage-receipt-unconfirmed-support' }]}
    >
      <GateStatus tone="warning" title={t('receiptUnconfirmed.title')} text={t('receiptUnconfirmed.description')} />
    </GateScreen>
  );
};

// ─── Payment window ran out after the client went for the receipt (CPAYMENT-1716) ──
// Клиент нажал «далее», то есть, вероятно, уже платил, но время тикета вышло. Экран отмены
// предлагает лишь вернуться к мерчанту и ничего не говорит про уже отправленный перевод.
export const GatePaymentExpiredScreen = ({
  uuid,
  timeLeft,
  onContactSupport,
}: {
  uuid: string;
  timeLeft: string;
  onContactSupport: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <GateScreen
      uuid={uuid}
      timeLeft={timeLeft}
      // Кнопка ведёт на сайт мерчанта и называется так же: под именем «обратиться в поддержку»
      // она отправляла клиента совсем не туда, куда он рассчитывал попасть.
      actions={[{ label: t('gate.returnToSite'), onClick: onContactSupport, variant: 'green', dataSentryElement: 'onepage-payment-expired-support' }]}
    >
      <GateStatus tone="warning" title={t('gate.expiredTitle')} text={t('gate.expiredText')} />
    </GateScreen>
  );
};

// ─── Error / Declined ─────────────────────────────────────────────────────────
export const GateErrorScreen = ({
  transaction,
  uuid,
  timeLeft,
  onFinish,
  receiptAccepted,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onFinish: () => void;
  receiptAccepted?: boolean;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <GateScreen uuid={uuid} actions={[{ label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true, dataSentryElement: 'onepage-final-finish' }]}>
      <GateStatus tone="error" title={t('error.paymentError')} />
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
      {receiptAccepted && (
        <SuccessRow>{t('upload.receiptAccepted')} <span>✓</span></SuccessRow>
      )}
    </GateScreen>
  );
};

// ─── Expired ──────────────────────────────────────────────────────────────────
export const GateExpiredScreen = ({
  transaction,
  uuid,
  onFinish,
}: {
  transaction: Transaction;
  uuid: string;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <GateScreen
      uuid={uuid}
      actions={[
        { label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true, dataSentryElement: 'onepage-error-finish' },
      ]}
    >
      {/* Обещания выслать чек здесь нет: кнопки для этого на экране не существует. */}
      <GateStatus tone="warning" title={t('gate.expiredTitle')} text={t('gate.expiredText')} />
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
    </GateScreen>
  );
};

// ─── Session expired ──────────────────────────────────────────────────────────
export const GateSessionExpiredScreen = ({
  transaction,
  uuid,
  onFinish,
}: {
  transaction: Transaction;
  uuid: string;
  onFinish: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <GateScreen uuid={uuid} actions={[{ label: t('common.finish'), onClick: onFinish, variant: 'grey', confirmLeave: true, dataSentryElement: 'onepage-final-finish' }]}>
      <GateStatus tone="error" title={t('error.sessionExpired')} />
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
    </GateScreen>
  );
};

// ─── Server error ─────────────────────────────────────────────────────────────
export const GateServerErrorScreen = ({
  transaction,
  uuid,
  timeLeft,
  onReturn,
}: {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onReturn: () => void;
}) => {
  const { t } = useTranslation();
  const method = transaction?.paymentMethod?.includes('SBP') ? 'SBP' : transaction?.paymentMethod;
  return (
    <GateScreen uuid={uuid} timeLeft={timeLeft} actions={[{ label: t('common.returnToMerchant'), onClick: onReturn, variant: 'grey', confirmLeave: true, dataSentryElement: 'onepage-server-error-return' }]}>
      <GateStatus tone="error" title={t('gate.serverError')} />
      <InfoRow>{t('common.paymentMethod')} {method}</InfoRow>
      <GreyCard>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GateSpinner size={24} />
          <span style={{ fontSize: 14, color: '#555' }}>{t('processing.pleaseWait')}</span>
        </div>
      </GreyCard>
    </GateScreen>
  );
};
